import { useHandler } from "@redotech/react-util/hook";
import { t } from "@redotech/redo-i18n";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { filterTruthy, remove } from "@redotech/util/array";
import { BackspaceKey, CommaKey, EnterKey } from "@redotech/web-util/key";
import classNames from "classnames";
import {
  ForwardedRef,
  forwardRef,
  memo,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { TextMeasureBox } from "../../common/text-measure-box";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { textClasses } from "../../theme/typography";
import { OverflowTooltip } from "../../tooltip/overflow-tooltip";
import { RedoBadge, RedoBadgeProps } from "../badge/redo-badge";
import * as redoTextInputCss from "./base-redo-input.module.css";
import {
  RedoInputSize,
  RedoInputState,
  sizeStyles,
  sizeToDescriptorTextProps,
  sizeToInputTextProps,
  sizeToPx,
} from "./base-redo-text-input";
import { BaseRedoInputInfoTooltip } from "./base-redo-text-input-info-tooltip";
import * as redoTagInputFieldCss from "./redo-tag-input-field.module.css";

type RedoTagInputTag = Omit<RedoBadgeProps, "size" | "iconTrailing"> & {
  text: string;
};

export const redoTagDelimiter = ["newline", "whitespace", "comma"] as const;
export type RedoTagDelimiter = (typeof redoTagDelimiter)[number];

function getDelimiterDisplayName(delimiter: RedoTagDelimiter): string {
  switch (delimiter) {
    case "newline":
      return t("enter");
    case "whitespace":
      return t("space");
    case "comma":
      return t("comma");
  }
}

export function formatDelimitersMessage(
  delimiters: RedoTagDelimiter[],
): string {
  const delimiterNames = delimiters.map((delimiter) =>
    getDelimiterDisplayName(delimiter),
  );

  const listFormatter = new Intl.ListFormat(undefined, {
    style: "long",
    type: "disjunction",
  });
  const delimiterList = listFormatter.format(delimiterNames);

  return t("Press {{delimiterList}} to add the value", { delimiterList });
}

interface RedoTagInputProps {
  autoFocus?: boolean;
  size?: RedoInputSize;
  placeholder?: string;
  tags: RedoTagInputTag[];
  setTags: (tags: string[]) => void;
  infoTooltip?: string;
  label?: string;
  description?: string;
  errorText?: string;
  state?: RedoInputState;
  delimiters?: RedoTagDelimiter[];
  /** Without fitContent, the input will be fixed at the size given by size. */
  fitContent?: boolean;
  inputValue: string;
  setInputValue: (value: string) => void;
  onBlur?: () => void;
}

export const RedoTagInputField = memo(
  forwardRef(function RedoTagInputField(
    {
      autoFocus,
      size = "sm",
      placeholder,
      tags,
      setTags,
      inputValue,
      setInputValue,
      infoTooltip,
      label,
      description,
      errorText,
      delimiters = ["newline"],
      state = "default",
      fitContent = true,
      onBlur,
    }: RedoTagInputProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) {
    const processInput = useHandler((text: string) => {
      const uniqueValues = extractValues(text, delimiters);
      setTags([...tags.map((t) => t.text), ...uniqueValues]);
      setInputValue("");
    });

    const handlePaste = useHandler(
      (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text");
        const input = e.currentTarget;
        const start = input.selectionStart ?? inputValue.length;
        const end = input.selectionEnd ?? inputValue.length;
        const combined =
          inputValue.slice(0, start) + pastedData + inputValue.slice(end);
        const { toCommit, remainder } = splitPastedInput(combined, delimiters);
        if (toCommit.length === 0) {
          setInputValue(remainder);
          return;
        }
        setTags([...tags.map((t) => t.text), ...toCommit]);
        setInputValue(remainder);
      },
    );

    const handleKeydown = useHandler(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === EnterKey && inputValue.trim()) {
          e.preventDefault();
        } else if (e.key === BackspaceKey && inputValue === "") {
          setTags(tags.slice(0, -1).map((t) => t.text));
        }
      },
    );

    const handleKeyUp = useHandler(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (delimiters.includes("newline") && e.key === EnterKey) {
          processInput(e.currentTarget.value);
        } else if (delimiters.includes("whitespace") && /\s/.test(e.key)) {
          processInput(e.currentTarget.value);
        } else if (delimiters.includes("comma") && e.key === CommaKey) {
          processInput(e.currentTarget.value);
        }
      },
    );

    const descriptorFontSize = sizeToDescriptorTextProps[size];

    const [textWidth, setTextWidth] = useState(0);

    const error = state === "error";
    const disabled = state === "disabled";
    const readonly = state === "readonly";

    const inputId = `redo-tag-input-field-${useId()}`;

    const [wrapperRef, setWrapperRef] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
      wrapperRef?.style.setProperty("--input-width", `${textWidth}px`);
    }, [wrapperRef, textWidth]);

    const measureText = useMemo(() => {
      const realPlaceholder = placeholder || "";
      return inputValue.length > realPlaceholder.length
        ? inputValue
        : realPlaceholder;
    }, [placeholder, inputValue]);

    return (
      <>
        <Flex
          data-arbiter="RedoTagInputField"
          dir="column"
          gap="xs"
          grow={1}
          ref={setWrapperRef}
          wrap="wrap"
        >
          {label && (
            <Text
              as="label"
              fontSize={descriptorFontSize}
              htmlFor={inputId}
              textColor={error ? "error" : "secondary"}
            >
              {label}
            </Text>
          )}
          <Flex
            align="center"
            bgColor="primary"
            className={classNames(
              redoTagInputFieldCss.growableInput,
              disabled && redoTextInputCss.disabled,
              redoTextInputCss.inputWrapper,
              error && redoTextInputCss.error,
              sizeStyles[size],
              fitContent && redoTextInputCss.fitContent,
              readonly && redoTextInputCss.readonly,
            )}
            dir="row"
            justify="flex-start"
            overflow="hidden"
            px={readonly ? undefined : sizeToPx[size]}
          >
            <Flex
              dir="row"
              gap="xs"
              grow={readonly ? undefined : 1}
              overflow="hidden"
              py="xxs"
              wrap="wrap"
            >
              {tags.map((tag, index) => {
                return (
                  <TagChip
                    disabled={disabled}
                    key={index}
                    onRemove={() =>
                      setTags(
                        remove(
                          tags.map((t) => t.text),
                          index,
                        ),
                      )
                    }
                    readonly={readonly}
                    tag={tag}
                  />
                );
              })}
              {!readonly && (
                <input
                  autoComplete="off"
                  autoFocus={autoFocus}
                  className={classNames(
                    textClasses(sizeToInputTextProps[size]),
                    redoTextInputCss.input,
                    redoTagInputFieldCss.textInput,
                  )}
                  disabled={disabled}
                  id={inputId}
                  name="off"
                  onBlur={onBlur}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeydown}
                  onKeyUp={handleKeyUp}
                  onPaste={handlePaste}
                  placeholder={placeholder}
                  ref={ref}
                  value={inputValue}
                />
              )}
            </Flex>
            {infoTooltip && (
              <BaseRedoInputInfoTooltip
                infoTooltip={infoTooltip}
                size={size}
                state={state}
              />
            )}
          </Flex>

          {description && (
            <Text
              fontSize={descriptorFontSize}
              textColor={error && !errorText ? "error" : "secondary"}
            >
              {description}
            </Text>
          )}
          {errorText && (
            <Text fontSize={descriptorFontSize} textColor="error">
              {errorText}
            </Text>
          )}
        </Flex>
        <TextMeasureBox
          setTextWidth={setTextWidth}
          text={measureText}
          textWidth={textWidth}
          {...sizeToInputTextProps[size]}
        />
      </>
    );
  }),
);

const TagChip = memo(function TagChip({
  tag,
  disabled,
  readonly,
  onRemove,
}: {
  tag: RedoTagInputTag;
  disabled: boolean;
  readonly: boolean;
  onRemove: () => void;
}) {
  const [textRef, setTextRef] = useState<HTMLElement | null>(null);
  return (
    <OverflowTooltip
      overflowRef={textRef}
      tooltipProps={{ title: tag.text }}
      wrapperStyle={{ minWidth: 0, overflow: "hidden" }}
    >
      <div
        className={classNames(
          (disabled || readonly) && redoTagInputFieldCss.disabled,
          redoTagInputFieldCss.tagContainer,
        )}
      >
        <RedoBadge
          iconTrailing={
            disabled || readonly
              ? undefined
              : {
                  Icon: XIcon,
                  ariaLabel: `Remove ${tag.text}`,
                  onClick: onRemove,
                  type: "icon",
                }
          }
          {...tag}
          setRef={setTextRef}
        />
      </div>
    </OverflowTooltip>
  );
});

function buildDelimiterRegex(delimiters: RedoTagDelimiter[]): RegExp {
  const hasWhitespace = delimiters.includes("whitespace");
  const hasNewline = delimiters.includes("newline");
  const hasComma = delimiters.includes("comma");
  if (hasWhitespace && hasComma) {
    return /[\s,]/;
  }
  if (hasWhitespace) {
    return /\s/;
  }
  if (hasNewline && hasComma) {
    return /[\n,]/;
  }
  if (hasComma) {
    return /,/;
  }
  return /\n/;
}

export function extractValues(
  text: string,
  delimiters: RedoTagDelimiter[],
): string[] {
  return filterTruthy(
    text.split(buildDelimiterRegex(delimiters)).map((value) => value.trim()),
  );
}

export function splitPastedInput(
  combined: string,
  delimiters: RedoTagDelimiter[],
): { toCommit: string[]; remainder: string } {
  const parts = combined.split(buildDelimiterRegex(delimiters));
  if (parts.length === 1) {
    return { toCommit: [], remainder: combined };
  }
  const toCommit = filterTruthy(
    parts.slice(0, -1).map((value) => value.trim()),
  );
  return { toCommit, remainder: parts[parts.length - 1] ?? "" };
}
